import React from 'react';
import { connect } from 'react-redux';

import { togglePopup } from 'apps/auth/actions/appAction';
import { Button } from 'apps/auth/components/common/Button/Button';
import { DiskButton } from 'apps/auth/components/common/DiskButton/DiskButton';
import Spinner from 'apps/auth/components/utils/Spinner/Spinner';
import type { RootState } from 'apps/auth/redux/store';

import type { PopupNewHandle } from './PopupNew';
import { ComplexButtonPanel, PopupBody, PopupBoxClose, SecondaryButton } from './styles';

type ButtonKind = 'link' | 'button' | 'submit';
type ButtonClickEvent = React.MouseEvent<HTMLElement>;

const parseTabIndex = (value?: number | string) => {
    if (typeof value === 'number') {
        return value;
    }
    if (typeof value === 'string') {
        const parsed = Number(value);
        return Number.isNaN(parsed) ? undefined : parsed;
    }
    return undefined;
};

const getButtonType = (primaryButtonType?: ButtonKind): ButtonKind => {
    switch (primaryButtonType) {
        case 'link':
        case 'submit':
            return primaryButtonType;
        case 'button':
        default:
            return 'button';
    }
};

type StateProps = {
    isShowSpinner?: boolean;
};

const mapStateToProps = (state: RootState): StateProps => {
    const appState = state.app as { isShowSpinner?: boolean } | undefined;
    return {
        isShowSpinner: appState?.isShowSpinner,
    };
};

type DispatchProps = {
    togglePopup: (isOpen: boolean) => void;
};

export interface ComplexPopupProps extends StateProps, DispatchProps {
    popupRef?: React.RefObject<PopupNewHandle>;
    handleClose?: () => void;
    withoutCloseButton?: boolean;
    textCenter?: boolean;
    hasBottomPanel?: boolean;
    hasPseudoTable?: boolean;
    withoutButton?: boolean;
    secondaryButtonText?: string;
    secondaryButtonType?: ButtonKind;
    secondaryButtonHref?: string;
    secondaryButtonTarget?: string;
    secondaryButtonTabIndex?: number | string;
    onSecondaryButtonClick?: (event: ButtonClickEvent) => void;
    primaryButtonText?: string;
    primaryButtonType?: ButtonKind;
    primaryButtonHref?: string;
    primaryButtonTarget?: string;
    primaryButtonTabIndex?: number | string;
    primaryButtonForm?: string;
    primaryButtonDisabled?: boolean;
    onPrimaryButtonClick?: (event: ButtonClickEvent) => void;
    formRef?: React.Ref<HTMLButtonElement>;
    precheckPopupButtons?: React.ReactNode;
    children?: React.ReactNode;
}

const ComplexPopupComponent: React.FC<ComplexPopupProps> = ({ popupRef = null, ...props }) => {
    const handleClose = () => {
        if (props.handleClose) {
            props.handleClose();
        } else if (popupRef?.current) {
            popupRef.current.close();
        } else {
            props.togglePopup(false);
        }
    };

    const renderSecondaryButton = () => {
        if (!props.secondaryButtonText) {
            return null;
        }
        return (
            <SecondaryButton>
                <Button
                    type={getButtonType(props.secondaryButtonType)}
                    href={props.secondaryButtonHref ?? undefined}
                    target={props.secondaryButtonTarget ?? undefined}
                    tabIndex={parseTabIndex(props.secondaryButtonTabIndex)}
                    size="fit"
                    variant="secondary"
                    raised
                    caption={props.secondaryButtonText}
                    onClick={(event) => {
                        props.onSecondaryButtonClick?.(event);
                    }}
                />
            </SecondaryButton>
        );
    };

    const renderPrimaryButton = () => {
        if (!props.primaryButtonText) {
            return null;
        }
        return (
            <Button
                type={getButtonType(props.primaryButtonType)}
                href={props.primaryButtonHref ?? undefined}
                target={props.primaryButtonTarget ?? undefined}
                tabIndex={parseTabIndex(props.primaryButtonTabIndex)}
                form={props.primaryButtonForm}
                disabled={props.primaryButtonDisabled}
                ref={props.formRef}
                size="fit"
                variant="primary"
                raised
                caption={props.primaryButtonText}
                onClick={(event) => {
                    props.onPrimaryButtonClick?.(event);
                }}
            />
        );
    };

    return (
        <>
            {!props.withoutCloseButton && (
                <PopupBoxClose>
                    <DiskButton onClick={handleClose} iconName="cross" iconColor="secondary" size="small" type="button" variant="tertiary" raised={false} />
                </PopupBoxClose>
            )}

            <PopupBody textCenter={props.textCenter} hasBottomPanel={props.hasBottomPanel} hasPseudoTable={props.hasPseudoTable}>
                {props.isShowSpinner && <Spinner forceShow insidePopup isEmptyText />}
                {props.children}
                {!props.withoutButton && (
                    <ComplexButtonPanel>
                        {renderSecondaryButton()}
                        {renderPrimaryButton()}
                        {props.precheckPopupButtons}
                    </ComplexButtonPanel>
                )}
            </PopupBody>
        </>
    );
};

export default connect(mapStateToProps, { togglePopup })(ComplexPopupComponent as React.ComponentType<any>);
